import React from "react";
import type { ApolloClient } from "@apollo/client";
import { isEmpty } from "lodash";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { TransactionsTable as TransactionsTableRaw } from "pages/dashboards/individualDashboard/transactions/TransactionsTable";
import { createErrorSelector } from "store/selectors";
import { getTransactionsForUser } from "actions/adminActions";
import { adminConstants } from "actions/types";
import { FiList } from "react-icons/fi";

import IconSpinner from "components/IconSpinner";
import Alert from "components/Alert";
import IconMessageCard from "components/IconMessageCard";
import type { Transaction } from "types/models";
import type { RootState } from "types/state";

// TransactionsTable's named export is the UNCONNECTED class; its props are the
// connector's ConnectedProps (which, with mapDispatchToProps=null, also include
// an injected `dispatch`). This page renders the raw class and supplies only
// `transactions`, relying on the class never reading `dispatch` — cast at the
// boundary to the prop subset actually passed.
const TransactionsTable = TransactionsTableRaw as React.ComponentType<{
  transactions: Transaction[];
}>;

const errorSelector = createErrorSelector(adminConstants.GET_USER_TRANSACTIONS);

const mapStateToProps = (state: RootState) => {
  return {
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  getTransactionsForUser,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  userId?: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  isFetching: boolean;
  transactions: Transaction[];
}

class UserTransactions extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isFetching: true,
      transactions: [],
    };
  }

  async componentDidMount() {
    const { data: transactions } = (await this.props.getTransactionsForUser(
      this.props.client as ApolloClient<object>,
      // userId is an optional prop (parent passes a possibly-undefined value);
      // the action param is string | number — preserve the original
      // unguarded call where undefined could flow to the ID! variable
      this.props.userId as string
    )) as { data?: Transaction[] };

    this.setState({
      // the action's success payload is the loosely-typed query result
      // (GetUserTransactionsQuery selection); TransactionsTable consumes the
      // full Transaction model — preserve the original runtime assignment
      transactions: transactions as unknown as Transaction[],
      isFetching: false,
    });
  }

  render() {
    if (this.state.isFetching) {
      return <IconSpinner centered />;
    }

    if (this.props.error) {
      return <Alert type={"error"} msg={this.props.error} />;
    }

    if (isEmpty(this.state.transactions)) {
      return (
        <div>
          <IconMessageCard
            header={"Empty Transactions"}
            subheader="No transactions exist for this user"
            icon={<FiList color="white" stroke="#60A4BF" size={16} />}
          />
        </div>
      );
    }

    return (
      <div id={"admin-user-transactions"}>
        <TransactionsTable transactions={this.state.transactions} />;
      </div>
    );
  }
}

export default connector(withApollo<Props>(UserTransactions));
